import {
  Box,
  BoxProps,
  ClickAwayListener,
  Divider,
  Paper,
  Popper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { round, sumBy } from "lodash";
import { useMemo, useRef, useState } from "react";

import { components } from "@/@types/api";
import { KeyboardArrowDown } from "@/Components/Icons/IconList";
import { HierarchicalAutocomplete } from "@/Components/v2/atoms/Autocomplete";
import { HierarchicalOption } from "@/Components/v2/atoms/Autocomplete/types";
import { OutlinedButton } from "@/Components/v2/atoms/Button";
import { MenuItem } from "@/Components/v2/atoms/MenuItem";
import { Select } from "@/Components/v2/atoms/Select";
import { HorizontalBarChart } from "@/Components/v2/molecule/BarChart/HorizontalBarChart";
import { graphColors } from "@/Components/v2/utils/brand-colors";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { ABBREVIATED_OPTION, useConfig } from "@/hooks/useConfig";
import { calculateROI } from "@/shared/utils/calculateROI";

import {
  DefaultBrandValue,
  DefaultCategoryValue,
  getBCPFromRevenues,
  getUniqueBrandsAndCategories,
  truncateText,
} from "../../../utils";
import { getRevenueBreakdownTooltip } from "../helpers/tooltip";

export enum MetricView {
  ROI = "roi",
  SpendVsRevenueDollars = "spend_vs_revenue_dollars",
  SpendVsRevenuePercentage = "spend_vs_revenue_percentage",
}

export type ScenarioResultRevenue = components["schemas"]["ScenarioResultRevenue"];
export type ScenarioResultSpend = components["schemas"]["ScenarioResultSpend"];

interface RevenueBreakdownProps extends BoxProps {
  revenues: ScenarioResultRevenue[];
  spends: ScenarioResultSpend[];
}

export type RevenueBreakdownLevel =
  | {
      level: "portfolio";
    }
  | {
      level: "brand";
      brand: string;
    }
  | {
      level: "category";
      brand: string;
      category: string;
    };

type RevenueSpendBreakdown = {
  label: string;
  revenue: number;
  spend?: number;
  revenuePercentage?: number;
  spendPercentage?: number;
  roi?: number | null;
};

const getSpendRevenueBreakdown = (
  revenues: ScenarioResultRevenue[],
  spends: ScenarioResultSpend[],
  breakdownLevel: RevenueBreakdownLevel
): RevenueSpendBreakdown[] => {
  // Portfolio Level - group by brand
  if (breakdownLevel.level === "portfolio") {
    const revenueMap = revenues.reduce(
      (acc, revenue) => {
        const key = revenue.saleBrand;
        acc[key] = (acc[key] || 0) + (revenue.revenue ?? 0);
        return acc;
      },
      {} as Record<string, number>
    );

    const spendMap = spends.reduce(
      (acc, spend) => {
        const key = spend.marketingSpendBrand;
        acc[key] = (acc[key] || 0) + (spend.spend ?? 0);
        return acc;
      },
      {} as Record<string, number>
    );
    const totalRevenue = sumBy(revenues, "revenue");
    const totalSpend = sumBy(spends, "spend");

    return Object.entries(revenueMap).map(([brand, revenue]) => ({
      label: brand,
      revenue,
      revenuePercentage: totalRevenue ? revenue / totalRevenue : 0,
      spend: spendMap[brand] ?? 0,
      spendPercentage: totalSpend ? (spendMap[brand] ?? 0) / totalSpend : 0,
      roi: calculateROI(spendMap[brand], revenue),
    }));
  }
  // Brand Level - group by category or product
  if (breakdownLevel.level === "brand") {
    const filteredRevenues = revenues.filter(
      (revenue) => revenue.saleBrand === breakdownLevel.brand
    );

    // Check if all categories for this brand are default
    const hasNonDefaultCategories = filteredRevenues.some(
      (revenue) => revenue.saleCategory !== DefaultCategoryValue
    );

    if (hasNonDefaultCategories) {
      // Group by category when there are non-default categories
      const revenueMap = filteredRevenues.reduce(
        (acc, revenue) => {
          const key = revenue.saleCategory;
          acc[key] = (acc[key] || 0) + (revenue.revenue ?? 0);
          return acc;
        },
        {} as Record<string, number>
      );

      return Object.entries(revenueMap).map(([category, revenue]) => ({
        label: category,
        revenue,
      }));
    } else {
      // Group by product when all categories are default
      const revenueMap = filteredRevenues.reduce(
        (acc, revenue) => {
          const key = revenue.saleProduct;
          acc[key] = (acc[key] || 0) + (revenue.revenue ?? 0);
          return acc;
        },
        {} as Record<string, number>
      );

      return Object.entries(revenueMap).map(([product, revenue]) => ({
        label: product,
        revenue,
      }));
    }
  }

  // Product Level - group by product
  const filteredRevenues = revenues.filter(
    (revenue) =>
      revenue.saleBrand === breakdownLevel.brand && revenue.saleCategory === breakdownLevel.category
  );

  const revenueMap = filteredRevenues.reduce(
    (acc, revenue) => {
      const key = revenue.saleProduct;
      acc[key] = (acc[key] || 0) + (revenue.revenue ?? 0);
      return acc;
    },
    {} as Record<string, number>
  );

  // Convert the map to an array of category summaries
  const revenueBreakdown = Object.entries(revenueMap).map(([category, revenue]) => ({
    label: category,
    revenue,
  }));

  return revenueBreakdown;
};

export const RevenueBreakdown = ({ revenues, spends, ...boxProps }: RevenueBreakdownProps) => {
  const theme = useTheme();
  const { config } = useConfig();
  const { formatCurrency } = config.currency;
  const buttonRef = useRef<HTMLButtonElement>(null);

  const bcp = getBCPFromRevenues(revenues);
  const { brands, categories } = getUniqueBrandsAndCategories(bcp);

  const { mapBcpName } = useDefaultNodeNameInfo();

  // Create hierarchical options structure (portfolio -> brands -> categories, no products)
  const hierarchicalOptions = useMemo((): HierarchicalOption[] => {
    const options: HierarchicalOption[] = [];

    // Add Portfolio level if there are multiple brands
    if (brands.size > 1) {
      options.push({
        id: 0,
        label: "Portfolio",
        value: "portfolio",
        selectable: true,
      });
    }

    // Add Brand level options
    Array.from(brands).forEach((brand, brandIndex) => {
      const brandId = brandIndex + 1;
      const brandOption: HierarchicalOption = {
        id: brandId,
        label: mapBcpName({ brand, category: "default", product: "default" }).brand,
        value: brand,
        selectable: true,
      };

      // Add categories for this brand (excluding default categories)
      const brandCategories = Array.from(categories.values()).filter(
        (cat) => cat.brand === brand && cat.category !== DefaultCategoryValue
      );

      if (brandCategories.length > 0) {
        brandOption.children = brandCategories.map(({ category }, catIndex) => ({
          id: brandId * 1000 + catIndex,
          label: mapBcpName({ brand, category, product: "default" }).category,
          value: `${brand}-${category}`,
          selectable: true,
        }));
      }

      options.push(brandOption);
    });

    return options;
  }, [brands, categories]);

  // Create a mapping from hierarchical option values to breakdown levels
  const breakdownLevelMap = useMemo(() => {
    const map = new Map<string, RevenueBreakdownLevel>();

    // Portfolio level
    map.set("portfolio", { level: "portfolio" });

    // Brand level
    Array.from(brands).forEach((brand) => {
      map.set(brand, { level: "brand", brand });
    });

    // Category level (excluding default categories)
    Array.from(categories.values())
      .filter(({ category }) => category !== DefaultCategoryValue)
      .forEach(({ brand, category }) => {
        map.set(`${brand}-${category}`, { level: "category", brand, category });
      });

    return map;
  }, [brands, categories]);

  const [selectedValue, setSelectedValue] = useState<string>(
    brands.size > 1 ? "portfolio" : Array.from(brands)[0] || DefaultBrandValue
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleClickAway = () => {
    setIsDropdownOpen(false);
  };

  const handleUnitSelectWithDropdown = (option: any) => {
    if (option) {
      setSelectedValue(option.value);
      setIsDropdownOpen(false);
      // Change the metric view to the default to avoid broken states (i.e. when no spend to show)
      setSelectedView(MetricView.SpendVsRevenueDollars);
    }
  };

  const breakdownLevel = breakdownLevelMap.get(selectedValue);

  const revenueBreakdown = breakdownLevel
    ? getSpendRevenueBreakdown(revenues, spends, breakdownLevel)
    : [];

  const hasSpend = revenueBreakdown.some(({ spend }) => spend !== undefined);

  const businessUnitRevenue = revenueBreakdown.reduce((acc, { revenue }) => acc + revenue, 0);

  const viewOptions = [
    {
      type: MetricView.ROI,
      label: "ROI (%)",
    },
    {
      type: MetricView.SpendVsRevenueDollars,
      label: `Spend vs Revenue (${config.currency.getSymbol()})`,
    },
    {
      type: MetricView.SpendVsRevenuePercentage,
      label: "Spend vs Revenue (%)",
    },
  ];

  const [selectedView, setSelectedView] = useState<MetricView>(MetricView.SpendVsRevenueDollars);
  const axisLabels = revenueBreakdown.map(({ label }) => truncateText(label));

  const tooltipOptions = getRevenueBreakdownTooltip(formatCurrency, selectedView);

  const chartSeries = useMemo(() => {
    if (selectedView === "roi") {
      return [
        {
          name: "ROI",
          data: revenueBreakdown.map(({ roi }) => ({
            value: roi ?? 0,
            itemStyle: {
              borderRadius: (roi ?? 0) > 0 ? [0, 5, 5, 0] : [5, 0, 0, 5],
            },
          })),
          color: theme.palette.roiGraph.layer.brand,
        },
      ];
    }

    const series = [
      {
        name: "Marketing Revenue",
        data: revenueBreakdown.map(({ revenue, revenuePercentage }) =>
          selectedView === MetricView.SpendVsRevenueDollars ? revenue : (revenuePercentage ?? 0)
        ),
        color: graphColors.graphPurple100,
        itemStyle: {
          borderRadius: [0, 5, 5, 0], // Curved right end for horizontal bars
        },
      },
    ];

    if (hasSpend) {
      series.unshift({
        name: "Marketing Spend",
        data: revenueBreakdown.map(({ spend, spendPercentage }) =>
          selectedView === MetricView.SpendVsRevenueDollars ? (spend ?? 0) : (spendPercentage ?? 0)
        ),
        color: graphColors.graphBlue100,
        itemStyle: {
          borderRadius: [0, 5, 5, 0], // Curved right end for horizontal bars
        },
      });
    }
    return series;
  }, [revenueBreakdown, selectedView, hasSpend]);

  const xAxisLabelFormatter = (value: number) => {
    switch (selectedView) {
      case MetricView.ROI:
        return `${round(value)}%`;
      case MetricView.SpendVsRevenueDollars:
        return formatCurrency(value, ABBREVIATED_OPTION);
      case MetricView.SpendVsRevenuePercentage:
        return `${round(value * 100, 2)}%`;
      default:
        return "";
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      border={`1px solid ${theme.palette.section.border}`}
      p={3}
      borderRadius={3}
      {...boxProps}
    >
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="column">
          <Typography variant="h5" fontWeight={600} mb={0.5}>
            Business Unit Breakdown
          </Typography>
          <Typography variant="body2" mb={2.5} maxWidth="600px">
            This is how our model estimates that revenue will be distributed across business units
            if you follow the optimal spend distribution.
          </Typography>
        </Stack>
        {hasSpend && (
          <Stack direction="column">
            <Typography variant="body1" fontWeight={600} mb={0.5}>
              Show:
            </Typography>
            <Select
              value={selectedView}
              onChange={(e) => setSelectedView(e.target.value)}
              size="small"
              sx={{
                width: "250px",
              }}
            >
              {viewOptions.map((option) => (
                <MenuItem key={option.type} value={option.type}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}
      </Stack>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="body2" fontWeight={600} mb={0.5}>
        Business Unit
      </Typography>

      <Stack direction="row" alignItems="center" gap={3}>
        <Box>
          <OutlinedButton
            ref={buttonRef}
            variant="outlined"
            startIcon={<KeyboardArrowDown />}
            onClick={() => setIsDropdownOpen(true)}
          >
            {hierarchicalOptions.find((opt) => opt.value === selectedValue)?.label ||
              hierarchicalOptions
                .find((opt) => opt.children?.some((child) => child.value === selectedValue))
                ?.children?.find((child) => child.value === selectedValue)?.label ||
              "Select Business Unit"}
          </OutlinedButton>

          {/* Business Unit Dropdown */}
          <Popper
            open={isDropdownOpen}
            anchorEl={buttonRef.current}
            placement="bottom-start"
            style={{ zIndex: 1300, width: 400, maxWidth: "100%" }}
          >
            <ClickAwayListener onClickAway={handleClickAway}>
              <Box>
                {hierarchicalOptions.length === 0 ? (
                  <Paper elevation={3} sx={{ mt: 1, p: 2 }}>
                    <Typography>No business units available</Typography>
                  </Paper>
                ) : (
                  <>
                    <HierarchicalAutocomplete
                      options={hierarchicalOptions}
                      onChange={handleUnitSelectWithDropdown}
                      disableClearable={true}
                      openOnFocus={true}
                      open={true}
                      value={selectedValue}
                      renderInput={(params) => (
                        <Box ref={params.InputProps.ref}>
                          <input {...params.inputProps} style={{ display: "none" }} />
                        </Box>
                      )}
                    />
                  </>
                )}
              </Box>
            </ClickAwayListener>
          </Popper>
        </Box>

        <Stack>
          <Typography variant="body2" fontWeight={600}>
            {breakdownLevel?.level === "portfolio"
              ? "Portfolio"
              : breakdownLevel?.level === "brand"
                ? mapBcpName({
                    brand: breakdownLevel.brand,
                    category: "default",
                    product: "default",
                  }).brand
                : breakdownLevel?.level === "category"
                  ? mapBcpName({
                      brand: breakdownLevel.brand,
                      category: breakdownLevel.category,
                      product: "default",
                    }).category
                  : "Revenue"}{" "}
            Revenue
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {formatCurrency(businessUnitRevenue, ABBREVIATED_OPTION)}
          </Typography>
        </Stack>
      </Stack>
      <HorizontalBarChart
        axisLabels={axisLabels}
        series={chartSeries}
        xAxisLabelFormatter={xAxisLabelFormatter}
        tooltipOptions={tooltipOptions}
      />
      <Typography fontSize="0.75rem" variant="body2" fontStyle="italic" mt={2}>
        Revenue split shows how sales are shared across brands. It doesn&apos;t adjust for overlap,
        such as when one brand&apos;s growth comes at the expense of another. Use this to guide
        high-level budget allocation across the portfolio, not to measure the net gain from each
        brand.
      </Typography>
    </Box>
  );
};
